import { useEffect } from "react";
import { useSuspenseQuery } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { Button } from "@mui/material";
import { useTranslations } from "use-intl";
import { tripQueryOptions } from "@/app/lib/hooks/useTrips";
import { OverviewCard } from "@/app/ui/trips/bookingFlow/tripDetails/OverviewCard";
import { TripPlanCard } from "@/app/ui/trips/bookingFlow/tripDetails/TripPlanCard";
import {
  InclusionsCard,
  ExclusionsCard,
} from "@/app/ui/trips/bookingFlow/tripDetails/FeaturesCards";
import { VisaPermissionsCard } from "@/app/ui/trips/bookingFlow/tripDetails/VisaPermissionsCard";
import { HotelsCard } from "@/app/ui/trips/bookingFlow/tripDetails/HotelsCard";
import { AdditionalInformationCard } from "@/app/ui/trips/bookingFlow/tripDetails/AdditionalInformationCard";
import { TermsAndConditionsCard } from "@/app/ui/trips/bookingFlow/tripDetails/TermsAndConditionsCard";
import { PersistentDrawer } from "@/app/ui/PersistentDrawer";
import { usePurchaseStateContext } from "@/app/lib/context/PurchaseStateContext";
import { rudderAnalytics, TripTrackingEvents } from "@/app/lib/rudderstack";
import { formatCurrency } from "@/app/lib/utils/currency";

export const Route = createFileRoute("/_authenticated/trips/$tripId/")({
  component: RouteComponent,
});

function RouteComponent() {
  const t = useTranslations();
  const { tripId } = Route.useParams();
  const navigate = Route.useNavigate();
  const { data } = useSuspenseQuery(tripQueryOptions(tripId));
  const { updatePurchaseState } = usePurchaseStateContext();

  useEffect(() => {
    rudderAnalytics.track(TripTrackingEvents.USER_VIEWED_TRIP_DETAILS, {
      trip_id: tripId,
      trip_name: data.name,
    });
  }, [tripId, data.name]);

  return (
    <>
      <div className="space-y-3">
        <OverviewCard trip={data} />
        <TripPlanCard itineraries={data.itineraries} />
        <InclusionsCard trip={data} />
        <ExclusionsCard trip={data} />
        <HotelsCard trip={data} />
        <VisaPermissionsCard countries={data.countries} />
        {data.additionalInformation ? (
          <AdditionalInformationCard content={data.additionalInformation} />
        ) : null}
        {data.termsAndConditions ? (
          <TermsAndConditionsCard content={data.termsAndConditions} />
        ) : null}
      </div>

      <PersistentDrawer>
        <dl className="mt-2 mb-4 grid grid-cols-2 gap-2 text-sm text-text-primary">
          <dt>{t("trips.totalPriceFrom")}</dt>
          <dd className="text-end font-bold">
            {formatCurrency(data.discountedPriceForAdultInADoubleRoom)}
          </dd>
        </dl>

        <Button
          fullWidth
          variant="contained"
          onClick={() => {
            rudderAnalytics.track(
              TripTrackingEvents.USER_PROCEEDED_TO_TRIP_AVAILABILITY,
              {
                trip_id: tripId,
                trip_name: data.name,
              },
            );
            updatePurchaseState("inProgress");
            navigate({ to: "./booking" });
          }}
        >
          {t("trips.continue")}
        </Button>
      </PersistentDrawer>
    </>
  );
}
